/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Checkbox, Col, Collapse, Form, Input, Row, Typography } from 'antd';
import LabelWithOriginalText from '../../../Forms/Fields/LabelWithOriginalText';
import { DeleteIcon } from '../../../Icons/DeleteIcon';
import { PlusIcon } from '../../../Icons/PlusIcon';
import { ArrowDownIcon } from '../../../Icons/ArrowDownIcon';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { ALPHANUMERIC_SPACE_REGEX } from '../../../../constants/regex';

// Constant declarations
const { Paragraph } = Typography;

const UserInfoNodeForm = ({ form }) => {
  const { t } = useTranslation();
  const { isEditingPrimaryLanguage, botLanguages, currentBotLanguage, primaryBotLanguage } = useBotLanguage();

  return (
    <Fragment>
      <LabelWithOriginalText label={t('Prompt text')} originalText={form.getFieldValue(['data', 'label', primaryBotLanguage])} />
      {
        botLanguages.map(lang => (
          <Form.Item
            label=""
            name={['data', 'label', lang]}
            hidden={currentBotLanguage !== lang}
            rules={[{ required: lang === primaryBotLanguage && isEditingPrimaryLanguage, message: t('Prompt text cannot be blank.') }]}
          >
            <Input
              size="large"
              placeholder={t('Enter a message the user will see before the form')}
            />
          </Form.Item>
        ))
      }

      <Paragraph className="mb-2">{t('Select the type of data you would like to collect from the user')}</Paragraph>

      <Form.Item
        label=""
        name={['data', 'includeEmail']}
        valuePropName="checked"
        className="mb-0"
      >
        <Checkbox disabled={!isEditingPrimaryLanguage}>{t('Include email')}</Checkbox>
      </Form.Item>

      <Form.Item
        label=""
        name={['data', 'includeFullName']}
        valuePropName="checked"
        className="mb-0"
      >
        <Checkbox disabled={!isEditingPrimaryLanguage}>{t('Include full name')}</Checkbox>
      </Form.Item>

      <Form.Item
        label=""
        name={['data', 'includePhone']}
        valuePropName="checked"
      >
        <Checkbox disabled={!isEditingPrimaryLanguage}>{t('Include phone number')}</Checkbox>
      </Form.Item>

      <Collapse
        bordered={false}
        expandIcon={({ isActive }) => <ArrowDownIcon rotate={isActive ? 180 : 0} />}
      >
        <Collapse.Panel
          header={t('Advanced Settings')}
          forceRender
        >
          <Row gutter={16}>
            <Col xs={24}>
              <Form.List
                name={['data', 'customUserInfoData']}
              >
                {(fields, { add, remove }, { errors }) => (
                  <Fragment>
                    <Paragraph className="mb-2">{t('Add custom data')}</Paragraph>

                    <div className="overflow-y-auto overflow-x-hidden" style={{ maxHeight: 385 }}>
                      {
                        fields.map(({ key, name, ...restField }, index) => (
                          <Form.Item key={key} className="mb-4">
                            <Row wrap={false} gutter={8} align="bottom">
                              <Col flex="auto">
                                <Row gutter={16}>
                                  <Col className="d-flex flex-column justify-content-end" sm={12} xs={24}>
                                    <LabelWithOriginalText originalText={form.getFieldValue(['data', 'customUserInfoData', name, 'question', primaryBotLanguage])} />
                                    {
                                      botLanguages.map(lang => (
                                        <Form.Item
                                          {...restField}
                                          name={[name, 'question', lang]}
                                          className="mb-0 hide-error"
                                          hidden={currentBotLanguage !== lang}
                                          rules={[{ required: lang === primaryBotLanguage && isEditingPrimaryLanguage }]}
                                        >
                                          <Input
                                            size="large"
                                            placeholder="Field"
                                            onKeyPress={(e) => {
                                              if (!ALPHANUMERIC_SPACE_REGEX.test(e.key)) {
                                                e.preventDefault();
                                                return false;
                                              }
                                            }}
                                          />
                                        </Form.Item>
                                      ))
                                    }
                                  </Col>

                                  <Col sm={12} xs={24}>
                                    <LabelWithOriginalText originalText={form.getFieldValue(['data', 'customUserInfoData', name, 'placeholder', primaryBotLanguage])} />
                                    {
                                      botLanguages.map(lang => (
                                        <Form.Item
                                          {...restField}
                                          name={[name, 'placeholder', lang]}
                                          className="mb-0 hide-error"
                                          hidden={currentBotLanguage !== lang}
                                          rules={[{ required: lang === primaryBotLanguage && isEditingPrimaryLanguage }]}
                                        >
                                          <Input size="large" placeholder="Prompt text" />
                                        </Form.Item>
                                      ))
                                    }
                                  </Col>
                                </Row>
                              </Col>

                              {
                                fields.length > 0 ? (
                                  <Col flex="none">
                                    <Button
                                      icon={<DeleteIcon />}
                                      size="large"
                                      type="text"
                                      onClick={() => remove(name)}
                                      disabled={!isEditingPrimaryLanguage}
                                    />
                                  </Col>
                                ) : null
                              }
                            </Row>
                          </Form.Item>
                        ))
                      }
                    </div>

                    <Row gutter={16}>
                      <Col sm={12} xs={24}>
                        <Button
                          type="dashed"
                          size="large"
                          block
                          className="mb-4"
                          onClick={() => add()}
                          icon={<PlusIcon />}
                          disabled={!isEditingPrimaryLanguage}
                        >
                          {t('Add new field')}
                        </Button>
                      </Col>
                    </Row>

                    <Form.ErrorList errors={errors} />
                  </Fragment>
                )}
              </Form.List>
            </Col>
          </Row>

          <LabelWithOriginalText label={t('Invalid input message')} originalText={form.getFieldValue(['data', 'validationFallbackMessage', primaryBotLanguage])} />
          {
            botLanguages.map(lang => (
              <Form.Item
                label=""
                name={['data', 'validationFallbackMessage', lang]}
                hidden={currentBotLanguage !== lang}
              >
                <Input
                  size="large"
                  placeholder={t('Enter a message for invalid input')}
                />
              </Form.Item>
            ))
          }

          <LabelWithOriginalText label={t('Submit button label')} originalText={form.getFieldValue(['data', 'submitLabel', primaryBotLanguage])} />
          {
            botLanguages.map(lang => (
              <Form.Item
                label=""
                name={['data', 'submitLabel', lang]}
                hidden={currentBotLanguage !== lang}
              >
                <Input
                  placeholder={t('Enter a submit label')}
                  size="large"
                />
              </Form.Item>
            ))
          }
        </Collapse.Panel>
      </Collapse>
    </Fragment>
  );
};

UserInfoNodeForm.propTypes = {
  form: PropTypes.object.isRequired
};

export default UserInfoNodeForm;
